import { useCallback, useEffect, useRef, useState } from "react";
import { settleAction } from "../utils/globalBusyTracker";

type BusyClickOptions = {
  /** Controlled loading (OR'd with internal busy). */
  loading?: boolean;
  disabled?: boolean;
  /** Skip auto-busy wrapping (explicit opt-out). */
  busyIgnore?: boolean;
  /** Delay before showing busy UI so instant sync clicks (menu open) do not flash. */
  busyDelayMs?: number;
};

/**
 * Wraps a click handler so the control stays in a busy state until the action
 * (and any API it triggers) finishes.
 */
export function useBusyClick<E>(
  onClick: ((event: E) => unknown) | undefined,
  options: BusyClickOptions = {}
) {
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const { loading = false, disabled = false, busyIgnore = false, busyDelayMs = 80 } = options;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleClick = useCallback(
    async (event: E) => {
      if (!onClick || busyIgnore) {
        onClick?.(event);
        return;
      }
      if (busyRef.current || disabled || loading) {
        const maybeEvent = event as { preventDefault?: () => void };
        maybeEvent.preventDefault?.();
        return;
      }
      busyRef.current = true;
      const showTimer = window.setTimeout(() => {
        if (busyRef.current && mountedRef.current) {
          setBusy(true);
        }
      }, busyDelayMs);
      try {
        await settleAction(onClick(event));
      } finally {
        window.clearTimeout(showTimer);
        busyRef.current = false;
        if (mountedRef.current) {
          setBusy(false);
        }
      }
    },
    [onClick, busyIgnore, disabled, loading, busyDelayMs]
  );

  return {
    busy: busy || loading,
    onClick: onClick ? handleClick : undefined,
  };
}
